/**
 * The `ar-aging` report (API: services/billing/ar-aging/) and a recorded
 * payment (`invoice-payments`). A hand-kept mirror of the API's types; keep
 * the two in step. Every amount is a DECIMAL(19,4) STRING, summed by the API
 * as integers — format it (shared/trackers/money.ts), never add two here.
 *
 * Pure (no React, no MUI).
 */

export const AGING_BUCKETS = ["current", "d1_30", "d31_60", "d61_90", "over90"] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

export const AGING_BUCKET_LABELS: Record<AgingBucket, string> = {
  current: "Current",
  d1_30: "1–30 days",
  d31_60: "31–60 days",
  d61_90: "61–90 days",
  over90: "Over 90 days",
};

export interface AgingTotals {
  invoiceCount: number;
  openCount: number;
  billed: string;
  received: string;
  paidUnrecorded: string;
  balance: string;
  overdue: string;
  overpaid: string;
  buckets: Record<AgingBucket, string>;
  oldestDueDate: string | null;
  lastPaymentOn: string | null;
}

export interface ClientAging extends AgingTotals {
  clientId: string;
  clientName: string | null;
  clientCode: string | null;
  archived: boolean;
}

export interface AgedInvoice {
  invoiceId: string;
  clientId: string;
  invoiceNumber: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  total: string;
  paid: string;
  balance: string;
  overpaid: string;
  paidUnrecorded: string;
  daysPastDue: number | null;
  bucket: AgingBucket | null;
  paymentCount: number;
  lastPaidOn: string | null;
}

export interface ArAging {
  key: string;
  clientId: string | null;
  asOf: string;
  timeZone: string;
  generatedAt: string;
  clients: ClientAging[];
  totals: AgingTotals;
  invoices: AgedInvoice[] | null;
}

export interface PaymentRecord {
  id: string;
  invoiceId: string;
  clientId: string;
  amount: string;
  receivedOn: string;
  method?: string | null;
  reference?: string | null;
  notes?: string | null;
  sourceSystem?: string | null;
  createdAt?: string;
}

/** The key of the whole-company report. */
export const ALL_CLIENTS = "all";
